import { NavigationTrapProps } from '@ds/react-utils'
import * as React from 'react'

type TrapConfig = Pick<
  NavigationTrapProps,
  'disableFocusReturn' | 'enabled' | 'initialElement'
>
const DISABLED_TRAP: TrapConfig = {
  enabled: false,
  disableFocusReturn: true,
  initialElement: undefined,
}

/**
 * Manages the initial focus within a modal when it becomes visible and is not animating.
 *
 * This hook determines the element that should receive initial focus within the modal
 * based on the `initialFocus` prop. It also manages the focus trap configuration to
 * ensure focus is correctly handled when the modal is visible and not animating.
 *
 * @param initialFocus - The element or selector to receive initial focus. Can be a string selector or a ref object.
 * @param showComplete - Indicates if the modal is finished animating.
 * @param visible - Indicates if the modal is visible.
 * @returns An object containing a ref for the default initially focused element and the trap configuration.
 */
export function useInitialFocus(
  initialFocus: string | React.RefObject<HTMLElement> | undefined,
  showComplete: boolean,
  visible: boolean,
): {
  defaultInitialFocusRef: React.RefObject<HTMLElement>
  trapConfig: TrapConfig
} {
  const defaultInitialFocusRef = React.useRef<HTMLElement>(null)

  let initialElementValue
  if (initialFocus) {
    initialElementValue =
      typeof initialFocus === 'string'
        ? document.querySelector<HTMLElement>(initialFocus)
        : initialFocus.current
  }
  const trapConfig = {
    ...DISABLED_TRAP,
    enabled: visible && showComplete,
    initialElement:
      initialElementValue ?? defaultInitialFocusRef.current ?? undefined,
  }

  return { defaultInitialFocusRef, trapConfig }
}
